/**
 * StaffOrganizations — IT-SCE Intern: Organisationen quer über alle Realms.
 *
 * TQ #9694 (Staff-Migration Teil A, Bundle #9523). Portiert aus
 * customer-portal/src/pages/staff/StaffOrganizations.tsx — gleiche
 * API-Calls (api.staffListOrganizations/staffApproveOrganization/…),
 * Styling an StaffKnowledge.tsx (org-portal, TQ #7128) angeglichen.
 *
 * Routing: outer-router-level (App.tsx) unter /staff/organizations, hinter
 * StaffOnly-Guard, gerendert über StaffCustomersHub (Tab "Organisationen").
 */

import { useEffect, useState } from 'react'
import { api, StaffOrganization } from '../../services/api'
import { useToast } from '../../components/ui'

const STATUS_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  active: { bg: '#d1fae5', fg: '#065f46', label: 'Aktiv' },
  pending_approval: { bg: '#fef3c7', fg: '#92400e', label: 'Ausstehend' },
  rejected: { bg: '#fee2e2', fg: '#991b1b', label: 'Abgelehnt' },
  suspended: { bg: '#f3f4f6', fg: '#374151', label: 'Suspendiert' },
}

export default function StaffOrganizations() {
  const [orgs, setOrgs] = useState<StaffOrganization[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const { showToast } = useToast()

  async function load() {
    setLoading(true)
    try {
      const resp = await api.staffListOrganizations()
      setOrgs(resp.organizations ?? [])
    } catch (err) {
      const msg = err instanceof Error ? err.message : String((err as { error?: string })?.error ?? err)
      showToast('error', `Laden fehlgeschlagen: ${msg}`)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function approve(o: StaffOrganization) {
    try {
      await api.staffApproveOrganization(o.id)
      showToast('success', `${o.name} freigegeben`)
      load()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String((err as { error?: string })?.error ?? err)
      showToast('error', `Fehler: ${msg}`)
    }
  }
  async function reject(o: StaffOrganization) {
    const reason = window.prompt(`${o.name} ablehnen — Grund?`)
    if (!reason) return
    try {
      await api.staffRejectOrganization(o.id, reason)
      showToast('success', `${o.name} abgelehnt`)
      load()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String((err as { error?: string })?.error ?? err)
      showToast('error', `Fehler: ${msg}`)
    }
  }

  const filtered = orgs.filter(o => {
    if (!search) return true
    const q = search.toLowerCase()
    return (o.name + ' ' + o.slug + ' ' + (o.keycloak_realm_name ?? '')).toLowerCase().includes(q)
  })

  return (
    <div>
      <p style={{ color: '#666', marginTop: 0, marginBottom: '1rem', fontSize: '0.9rem' }}>
        Alle Organisationen quer über alle Realms ({orgs.length})
      </p>

      <input
        type="search"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Suchen…"
        style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 16 }}
      />

      {loading ? (
        <p style={{ color: '#666' }}>Lade Organisationen…</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {filtered.map(o => {
            const badge = STATUS_BADGE[o.status] ?? { bg: '#f3f4f6', fg: '#374151', label: o.status }
            return (
              <div key={o.id} style={{ padding: 16, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>{o.name}</div>
                    <div style={{ color: '#6b7280', fontSize: 13 }}>
                      <code>{o.slug}</code>
                      {o.keycloak_realm_name && <> · Realm <code>{o.keycloak_realm_name}</code></>}
                    </div>
                  </div>
                  <span style={{ padding: '2px 8px', background: badge.bg, color: badge.fg, borderRadius: 12, fontSize: 12 }}>
                    {badge.label}
                  </span>
                </div>
                {o.description && (
                  <div style={{ color: '#374151', fontSize: 13, marginBottom: 8 }}>{o.description}</div>
                )}
                <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 8 }}>
                  {o.member_count != null && <>{o.member_count} Mitglied{o.member_count === 1 ? '' : 'er'} · </>}
                  Erstellt {new Date(o.created_at).toLocaleDateString('de-DE')}
                </div>
                {o.status === 'pending_approval' && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => approve(o)}
                      style={{ flex: 1, padding: '6px 12px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}
                    >
                      Freigeben
                    </button>
                    <button
                      onClick={() => reject(o)}
                      style={{ flex: 1, padding: '6px 12px', background: '#fff', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}
                    >
                      Ablehnen
                    </button>
                  </div>
                )}
              </div>
            )
          })}
          {filtered.length === 0 && (
            <div style={{ gridColumn: '1/-1', padding: 32, textAlign: 'center', color: '#9ca3af' }}>
              Keine Organisationen gefunden.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
